import { useMutation, useQuery } from '@apollo/client';
import { Col, Row } from 'antd';
import Text from 'antd/lib/typography/Text';
import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CustomNotification } from 'src/components/custom-notification';
import YouTubeVideoCard from 'src/components/ui/youtube-videos/youtube-video-card';
import { ROUTES } from 'src/config/route';
import { YouTubeVideoForm } from 'src/lib/form-interfaces';
import {
	MutationDeleteYouTubeVideo,
	MutationUpdateYouTubeVideo,
} from 'src/lib/gql/mutations';
import { QueryGetYouTubeVideo } from 'src/lib/gql/queries';

const EditYouTubeVideo = () => {
	const { id } = useParams();
	const navigate = useNavigate();

	const { data: qryData } = useQuery(QueryGetYouTubeVideo, {
		variables: { id },
		fetchPolicy: 'cache-and-network',
	});

	const [UpdateYouTubeVideo] = useMutation(MutationUpdateYouTubeVideo, {
		onError(error) {
			CustomNotification({
				pageName: 'YouTube Videos',
				pagePrefix: 'YouTube Video',
				notificationType: 'custom-error',
				customDescription: error.message,
			});
		},
	});

	const [DeleteYouTubeVideo] = useMutation(MutationDeleteYouTubeVideo, {
		onError(error) {
			CustomNotification({
				pageName: 'YouTube Videos',
				pagePrefix: 'YouTube Video',
				notificationType: 'custom-error',
				customDescription: error.message,
			});
		},
	});

	const updateQry = useCallback(
		async (data: YouTubeVideoForm) => {
			// Auto-generate thumbnail URL from video ID
			const thumbnailUrl = `https://img.youtube.com/vi/${data.videoId}/maxresdefault.jpg`;

			const rsp = await UpdateYouTubeVideo({
				variables: {
					id: id as string,
					videoId: data.videoId,
					title: data.title,
					description: data.description || null,
					duration: data.duration,
					thumbnailUrl: thumbnailUrl,
					weight: data.weight ? parseInt(data.weight.toString()) : null,
					youTubeVideoHTags: data.tags || [],
				},
			});

			if (rsp.data) {
				CustomNotification({
					pageName: 'YouTube Video',
					pagePrefix: 'YouTube Video',
					notificationType: 'updated',
				});
			}
		},
		[UpdateYouTubeVideo, id],
	);

	const deleteQry = useCallback(async () => {
		if (!confirm('Are you sure you want to delete this YouTube video?')) return;

		const rsp = await DeleteYouTubeVideo({
			variables: {
				id: id as string,
			},
		});

		if (rsp.data) {
			CustomNotification({
				pageName: 'YouTube Video',
				pagePrefix: 'YouTube Video',
				notificationType: 'deleted',
			});

			navigate(ROUTES.YOUTUBE_VIDEOS.path);
		}
	}, [DeleteYouTubeVideo, id, navigate]);

	return (
		<Row>
			<Col span={24}>
				<Text className="sub-header">Edit YouTube Video</Text>
			</Col>
			<Row style={{ width: '100%' }} gutter={16}>
				<YouTubeVideoCard
					youtubeVideoId={qryData?.getYouTubeVideo?.videoId}
					youtubeVideoTitle={qryData?.getYouTubeVideo?.title}
					youtubeVideoDescription={qryData?.getYouTubeVideo?.description}
					youtubeVideoDuration={qryData?.getYouTubeVideo?.duration}
					youtubeVideoWeight={qryData?.getYouTubeVideo?.weight || undefined}
					youtubeVideoTags={qryData?.getYouTubeVideo?.youTubeVideoHTags || []}
					canDelete={true}
					onDelete={deleteQry}
					onSave={updateQry}
				/>
			</Row>
		</Row>
	);
};

export default EditYouTubeVideo;